// MobileBottomNav — Barre de navigation mobile unifiée
// 5 icônes: Actu, Boutiques, Cockpit (surélevé orange), Acheter, Plus (...)
// Style Bleu Marine #1E3A5F
// IMMERSIVE SCROLL: Navbar disparaît au scroll down, réapparaît au scroll up

import { useNavigate, useLocation } from 'react-router-dom';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';
import { useNavMode, NAV_MODES } from '../../context/NavModeContext';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronRight, UserPlus, LayoutDashboard, MessageCircle, 
  Calendar, Image, Briefcase, User, Building2, Mic, Wand2, Film, Users, FileText
} from 'lucide-react';
import VoiceNotesSuperpouvoir from '../ai/VoiceNotesSuperpouvoir';

// Couleurs
const INACTIVE_COLOR = 'rgba(255,255,255,0.85)';
const ACTIVE_COLOR = '#F97316';
const BG_COLOR = '#1E3A5F';

// SVG Icons personnalisées
const ActuIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
    <path d="M18 14h-8"/>
    <path d="M15 18h-5"/>
    <path d="M10 6h8v4h-8V6Z"/>
  </svg>
);

const BoutiqueIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const CockpitIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const AcheterIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

const MoreIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1"/>
    <circle cx="19" cy="12" r="1"/>
    <circle cx="5" cy="12" r="1"/>
  </svg>
);

const CarteIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

// Icône Réseau
const ReseauIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

// Icône Vidéo (TikTok style)
const VideoIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
    <path d="m10 8 6 4-6 4V8Z"/>
  </svg>
);

// Icône Photo (Galerie)
const PhotoIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

// Icône Blog
const BlogIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    <line x1="8" y1="6" x2="16" y2="6"/>
    <line x1="8" y1="10" x2="16" y2="10"/>
    <line x1="8" y1="14" x2="12" y2="14"/>
  </svg>
);

// Icône Accueil
const AccueilIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

// Icône Shop (boutique)
const ShopIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

// Icône Event (calendrier)
const EventIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <path d="M8 14h.01"/>
    <path d="M12 14h.01"/>
    <path d="M16 14h.01"/>
    <path d="M8 18h.01"/>
    <path d="M12 18h.01"/>
  </svg>
);

// Icône Neuf (sac shopping)
const NeufIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

// Icône Annonces (panneau/clipboard avec liste)
const OccasionIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M8 7h8"/>
    <path d="M8 11h8"/>
    <path d="M8 15h5"/>
  </svg>
);

// Icône Chez moi (maison avec utilisateur)
const ChezMoiIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <circle cx="12" cy="13" r="3"/>
    <path d="M12 16v4"/>
  </svg>
);

// Items de navigation principaux SHOP - 5 items
const NAV_ITEMS_MAIN = [
  { id: 'accueil', IconComp: AccueilIcon, label: 'Accueil', path: '/' },
  { id: 'artisans', IconComp: BoutiqueIcon, label: 'Artisans', path: '/artisans' },
  { id: 'neuf', IconComp: NeufIcon, label: 'Neuf', path: '/produits' },
  { id: 'occasion', IconComp: OccasionIcon, label: 'Annonces', path: '/entre-particuliers' },
  { id: 'chezmoi', IconComp: ChezMoiIcon, label: 'Chez moi', path: '/cockpit-particulier' },
];

// Items de navigation RÉSEAU - 5 items (Actu, Vidéo, Photo, Blog, Event)
const NAV_ITEMS_RESEAU = [
  { id: 'actu-reseau', IconComp: ActuIcon, label: 'Actu', path: '/reseau' },
  { id: 'video', IconComp: VideoIcon, label: 'Vidéo', path: '/reseau/cinema' },
  { id: 'photo', IconComp: PhotoIcon, label: 'Photo', path: '/reseau/galerie' },
  { id: 'blog', IconComp: BlogIcon, label: 'Blog', path: '/reseau/blog' },
  { id: 'event', IconComp: EventIcon, label: 'Event', path: '/reseau/evenements' },
];

// Icône Espace (profil utilisateur)
const EspaceIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

// Icône Marketplace (sac shopping)
const MarketplaceIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

// Icône Service (clé/outil)
const ServiceIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
);

// Icône Emploi (mallette)
const EmploiIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

// Icône Réseau Cockpit (groupe)
const ReseauCockpitIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

// Items de navigation COCKPIT PARTICULIER - 5 items avec contexte hub
const NAV_ITEMS_COCKPIT = [
  { id: 'espace', IconComp: EspaceIcon, label: 'Espace', path: '/cockpit-particulier', hubContext: 'hub_espace' },
  { id: 'marketplace', IconComp: MarketplaceIcon, label: 'Marketplace', path: '/cockpit-particulier', hubContext: 'hub_marketplace' },
  { id: 'reseau', IconComp: ReseauCockpitIcon, label: 'Réseau', path: '/cockpit-particulier', hubContext: 'hub_reseau' },
  { id: 'service', IconComp: ServiceIcon, label: 'Service', path: '/cockpit-particulier', hubContext: 'hub_prestataires' },
  { id: 'emploi', IconComp: EmploiIcon, label: 'Emploi', path: '/cockpit-particulier', hubContext: 'hub_emploi' },
];

// Items du menu "Plus" (plus utilisé mais gardé pour compatibilité)
const PLUS_MENU_ITEMS = [
  { id: 'cinema', label: 'Cinéma', icon: Film, path: '/reseau/cinema' },
  { id: 'galerie', label: 'Galerie Photo', icon: Image, path: '/reseau/galerie' },
  { id: 'salon', label: 'Salon de discussion', icon: MessageCircle, path: '/reseau/salon' },
  { id: 'blog', label: 'Blog', icon: FileText, path: '/reseau/blog' },
  { id: 'evenements', label: 'Événements', icon: Calendar, path: '/reseau/evenements' },
  { id: 'emploi', label: 'Emploi', icon: Briefcase, path: '/emploi' },
];

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isHidden } = useImmersiveScrollContext();
  const { mode, setMode, isShopMode, isReseauMode } = useNavMode();
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showCockpitModal, setShowCockpitModal] = useState(false);
  const [showAcheterModal, setShowAcheterModal] = useState(false);
  const [showSuperpouvoir, setShowSuperpouvoir] = useState(false);
  const [showChatTest, setShowChatTest] = useState(false); // TEST: Chat style Claude mobile
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Écouter la classe modal-fullscreen-open sur le body pour masquer la navbar
  useEffect(() => {
    const checkModalOpen = () => {
      setIsModalOpen(document.body.classList.contains('modal-fullscreen-open'));
    };
    
    // Observer les changements de classe sur le body
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    // Vérifier l'état initial
    checkModalOpen();
    
    return () => observer.disconnect();
  }, []);
  
  // Récupérer l'ID utilisateur pour le superpouvoir
  const getUserId = () => {
    try {
      const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.id;
      }
    } catch {}
    return null;
  };
  
  // Déterminer l'item actif selon la route
  const getActiveItem = () => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const hubParam = searchParams.get('hub');
    
    // Cockpit Particulier - vérifier le paramètre hub
    if (path === '/cockpit-particulier' || path.startsWith('/cockpit-particulier')) {
      if (hubParam === 'hub_espace') return 'espace';
      if (hubParam === 'hub_marketplace') return 'marketplace';
      if (hubParam === 'hub_reseau') return 'reseau';
      if (hubParam === 'hub_prestataires') return 'service';
      if (hubParam === 'hub_emploi') return 'emploi';
      // Par défaut Espace
      if (path === '/cockpit-particulier') return 'espace';
    }
    // Réseau
    if (path === '/reseau' || path === '/reseau/trending' || path === '/reseau/actus') return 'actu';
    if (path === '/reseau/cinema' || path.includes('/reseau/cinema')) return 'cinema';
    if (path === '/artisans' || path.startsWith('/artisans/') || path.startsWith('/artisan/')) return 'artisans';
    if (path === '/marketplace' || path === '/produits' || path.startsWith('/produits/') || path.startsWith('/marketplace')) return 'marketplace';
    if (path === '/emploi' || path.startsWith('/emploi/')) return 'emploi';
    if (path.includes('/reseau/salon')) return 'salon';
    if (path.includes('/reseau/evenements')) return 'evenements';
    if (path.includes('/reseau/galerie')) return 'galerie';
    return null;
  };
  
  const activeItem = getActiveItem();
  
  // Détecter si on est dans le Cockpit Particulier
  const isCockpitParticulier = location.pathname.startsWith('/cockpit-particulier');
  // Masquer sur les pages boutique publique (elles ont leur propre bottom nav)
  const isBoutiquePage = location.pathname.startsWith('/boutique/');

  // Handler pour le clic sur un item
  const handleItemClick = (item) => {
    // Switch vers navbar Réseau + ouvre /reseau
    if (item.switchToReseau) {
      setMode(NAV_MODES.RESEAU);
      if (item.path) {
        navigate(item.path);
      }
      return;
    }
    // Switch vers navbar principale (Shop) + ouvre Accueil
    if (item.switchToMain) {
      setMode(NAV_MODES.SHOP);
      if (item.path) {
        navigate(item.path);
      }
      return;
    }
    // Items du cockpit avec hubContext - naviguer avec le paramètre hub
    if (item.hubContext) {
      navigate(`/cockpit-particulier?hub=${item.hubContext}`);
      return;
    }
    if (item.id === 'acheter') {
      // Acheter - ouvrir le modal de choix
      setShowAcheterModal(true);
    } else if (item.isSpecial && item.id === 'cockpit') {
      // Abra - ouvrir le modal de choix cockpit
      setShowCockpitModal(true);
    } else if (item.isSpecial && item.path) {
      // Item spécial avec path (ex: Photo au centre)
      navigate(item.path);
    } else if (item.path) {
      navigate(item.path);
    }
  };
  
  // Obtenir les items de nav selon le mode
  const currentNavItems = isCockpitParticulier 
    ? NAV_ITEMS_COCKPIT 
    : (isReseauMode ? NAV_ITEMS_RESEAU : NAV_ITEMS_MAIN);

  // Handler pour les items du menu Plus
  const handlePlusItemClick = (item) => {
    setShowPlusMenu(false);
    if (item.isCockpit) {
      setShowCockpitModal(true);
    } else if (item.path) {
      navigate(item.path);
    }
  };
  
  // Navigation vers un cockpit avec ouverture du menu
  const handleCockpitChoice = (type) => {
    setShowCockpitModal(false);
    if (type === 'particulier') {
      navigate('/cockpit-particulier?menu=open');
    } else {
      navigate('/cockpit-pro?menu=open');
    }
  };
  
  // Navigation vers Acheter (Prestataire ou Produit ou Entre Particuliers)
  const handleAcheterChoice = (type) => {
    setShowAcheterModal(false);
    if (type === 'prestataire') {
      navigate('/prestations');
    } else if (type === 'particuliers') {
      navigate('/entre-particuliers');
    } else {
      navigate('/marketplace');
    }
  };

  // Render un item de navigation
  const renderNavItem = (item) => {
    const isActive = activeItem === item.id || (item.id === 'actu-reseau' && activeItem === 'actu');
    const IconComp = item.IconComp;
    const itemColor = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
    
    // COCKPIT (Abra) - Bouton spécial orange surélevé
    if (item.isSpecial && item.id === 'cockpit') {
      return (
        <button
          key={item.id}
          onClick={() => handleItemClick(item)}
          className="flex flex-col items-center justify-center transition-all duration-150 ease-out"
          style={{ cursor: 'pointer', marginTop: '-20px' }}
          data-testid={`mobile-nav-${item.id}`}
        >
          {/* Carré orange avec bords arrondis, surélevé */}
          <div 
            className="flex items-center justify-center shadow-lg"
            style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)',
            }}
          >
            <IconComp color="#FFFFFF" />
          </div>
          <span 
            className="transition-all duration-150 text-center whitespace-nowrap mt-1"
            style={{ 
              color: '#F97316',
              fontSize: '9px',
              fontWeight: 700,
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: '0.01em',
            }}
          >
            {item.label}
          </span>
        </button>
      );
    }
    
    return (
      <button
        key={item.id}
        onClick={() => handleItemClick(item)}
        className="flex flex-col items-center justify-center gap-[2px] transition-all duration-150 ease-out nav-item-btn h-full"
        style={{ cursor: 'pointer' }}
        data-testid={`mobile-nav-${item.id}`}
      >
        <span className="flex items-center justify-center transition-all duration-150">
          <IconComp color={itemColor} />
        </span>
        <span 
          className="transition-all duration-150 text-center whitespace-nowrap"
          style={{ 
            color: itemColor,
            fontSize: '9px',
            fontWeight: isActive ? 700 : 600,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: '0.01em',
          }}
        >
          {item.label}
        </span>
      </button>
    );
  };
  
  // Ne pas afficher la nav si un modal fullscreen est ouvert
  if (isModalOpen) {
    return null;
  }

  return (
    <>
      {/* Note: Les boutons flottants (Cockpit, Superpouvoir) sont maintenant gérés 
          par CockpitFAB dans App.js pour éviter les doublons */}
      
      <nav
        className={`fixed bottom-0 left-0 right-0 z-[9600] md:hidden ${isHidden || isBoutiquePage ? 'nav-hidden' : ''}`}
        style={{
          background: BG_COLOR,
          borderTop: '2px solid #F97316',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHidden || isBoutiquePage ? 'translateY(100%)' : 'translateY(0)',
        }}
        data-testid="mobile-bottom-nav"
      >
        {/* Container avec hauteur adaptative selon la taille d'écran */}
        <div 
          className={`grid grid-cols-5 items-center justify-items-center samsung-nav-height`}
          style={{ 
            height: '68px',
          }}
        >
          {currentNavItems.map(renderNavItem)}
        </div>
        {/* Safe area padding en dessous */}
        <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
        
        {/* CSS pour Samsung/petits écrans < 420px */}
        <style>{`
          @media (max-width: 419px) {
            .samsung-nav-height {
              height: 56px !important;
            }
          }
        `}</style>
        
        {/* Style pour hover/active */}
        <style>{`
          .nav-item-btn:active span {
            transform: scale(0.95);
          }
        `}</style>
      </nav>

      {/* Menu Plus (Bottom Sheet) */}
      <AnimatePresence>
        {showPlusMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
            onClick={() => setShowPlusMenu(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Liste des items Réseau */}
              <div className="px-4 space-y-1">
                {PLUS_MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeItem === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handlePlusItemClick(item)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-orange-50 border-2 border-orange-200' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isActive 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-[#1E3A5F]/10 text-[#1E3A5F]'
                      }`}>
                        <Icon size={20} />
                      </div>
                      <span className={`flex-1 text-left font-medium ${
                        isActive ? 'text-orange-600' : 'text-gray-800'
                      }`}>
                        {item.label}
                      </span>
                      <ChevronRight size={20} className="text-gray-400" />
                    </button>
                  );
                })}
              </div>
              
              {/* Bouton Fermer */}
              <div className="px-4 mt-4">
                <button
                  onClick={() => setShowPlusMenu(false)}
                  className="w-full py-4 text-center font-semibold text-gray-600 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de choix Cockpit */}
      <AnimatePresence>
        {showCockpitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
            onClick={() => setShowCockpitModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="px-6 pb-4 text-center">
                <h2 className="text-xl font-bold text-gray-900">Choisis ton centre de pilotage</h2>
                <p className="text-sm text-gray-500 mt-1">Ton espace de gestion personnalisé</p>
              </div>
              
              {/* Options */}
              <div className="px-4 space-y-3">
                {/* Cockpit Particulier */}
                <button
                  onClick={() => handleCockpitChoice('particulier')}
                  className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-2xl border-2 border-orange-200 hover:border-orange-400 transition-all active:scale-[0.98]"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30 flex-shrink-0">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-bold text-gray-900 text-base">Cockpit Particulier</h3>
                    <p className="text-xs text-gray-600">Facilite ta vie</p>
                  </div>
                  <ChevronRight size={22} className="text-orange-500 flex-shrink-0" />
                </button>
                
                {/* Cockpit Pro */}
                <button
                  onClick={() => handleCockpitChoice('pro')}
                  className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-[#1E3A5F]/5 to-[#1E3A5F]/10 rounded-2xl border-2 border-[#1E3A5F]/20 hover:border-[#1E3A5F]/50 transition-all active:scale-[0.98]"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center shadow-lg shadow-[#1E3A5F]/30 flex-shrink-0">
                    <Building2 size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-bold text-gray-900 text-base">Cockpit Professionnel</h3>
                    <p className="text-xs text-gray-600">Facilite ton entreprise</p>
                  </div>
                  <ChevronRight size={22} className="text-[#1E3A5F] flex-shrink-0" />
                </button>
              </div>
              
              {/* Bouton Annuler */}
              <div className="px-4 mt-4">
                <button
                  onClick={() => setShowCockpitModal(false)}
                  className="w-full py-4 text-center font-semibold text-gray-600 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal de choix Acheter (Prestataire vs Produit) */}
      <AnimatePresence>
        {showAcheterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
            onClick={() => setShowAcheterModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="px-6 pb-4 text-center">
                <h2 className="text-xl font-bold text-gray-900">Que cherches-tu ?</h2>
                <p className="text-sm text-gray-500 mt-1">Produits, services ou petites annonces</p>
              </div>
              
              {/* Options */}
              <div className="px-4 space-y-3">
                {/* Acheter à un pro */}
                <button
                  onClick={() => handleAcheterChoice('produit')}
                  className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-2xl border-2 border-orange-200 hover:border-orange-400 transition-all active:scale-[0.98]"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30 flex-shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-bold text-gray-900 text-base">Acheter à un pro</h3>
                    <p className="text-xs text-gray-600">Équipements, outillage, fournitures pro</p>
                  </div>
                  <ChevronRight size={22} className="text-orange-500 flex-shrink-0" />
                </button>
                
                {/* Entre particuliers */}
                <button
                  onClick={() => handleAcheterChoice('particuliers')}
                  className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all active:scale-[0.98]"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 flex-shrink-0">
                    <Users size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-bold text-gray-900 text-base">Entre particuliers</h3>
                    <p className="text-xs text-gray-600">Acheter, vendre, louer, échanger, donner</p>
                  </div>
                  <ChevronRight size={22} className="text-emerald-500 flex-shrink-0" />
                </button>
              </div>
              
              {/* Bouton Annuler */}
              <div className="px-4 mt-4">
                <button
                  onClick={() => setShowAcheterModal(false)}
                  className="w-full py-4 text-center font-semibold text-gray-600 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal Superpouvoir Notes Vocales */}
      <AnimatePresence>
        {showSuperpouvoir && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowSuperpouvoir(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg"
            >
              <VoiceNotesSuperpouvoir 
                userId={getUserId()} 
                onClose={() => setShowSuperpouvoir(false)} 
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* TEST: Modal Chat Style Claude Mobile - À SUPPRIMER */}
      <AnimatePresence>
        {showChatTest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100000] bg-[#F9FAFB] flex flex-col"
          >
            {/* État pour la sidebar et le panel artefacts */}
            <ChatClaudeMobileTest onClose={() => setShowChatTest(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEST: Composant Chat Style Claude Mobile - À SUPPRIMER APRÈS TEST
// ═══════════════════════════════════════════════════════════════════════════════
function ChatClaudeMobileTest({ onClose }) {
  const [showSidebar, setShowSidebar] = useState(false);
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Salut ! Comment puis-je t\'aider aujourd\'hui ?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [conversations, setConversations] = useState([
    { id: 1, title: 'Devis rénovation cuisine', date: 'Aujourd\'hui' },
    { id: 2, title: 'Recherche plombier Paris', date: 'Aujourd\'hui' },
    { id: 3, title: 'Planning chantier Dupont', date: 'Hier' },
    { id: 4, title: 'Facture client Martin', date: 'Hier' },
  ]);

  const deleteConversation = (id) => {
    setConversations(conversations.filter(c => c.id !== id));
  };

  // Fonction pour nettoyer le markdown des réponses (enlever les ** et autres)
  const cleanMarkdown = (text) => {
    return text
      .replace(/\*\*/g, '')  // Enlever les **
      .replace(/\*/g, '')    // Enlever les *
      .replace(/##/g, '')    // Enlever les ##
      .replace(/#/g, '')     // Enlever les #
      .trim();
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* ═══ HEADER STYLE ABRACADAWORLD ═══ */}
      <div 
        className="flex items-center justify-between px-3 py-2.5 border-b"
        style={{ 
          background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
          borderColor: '#E8650A'
        }}
      >
        <div className="flex items-center gap-2">
          {/* Bouton ouvrir sidebar */}
          <button
            onClick={() => setShowSidebar(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
          </button>
          
          {/* Logo + Titre */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E8650A] to-[#F97316] flex items-center justify-center">
              <Wand2 size={16} className="text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">Mon Assistant</span>
              <p className="text-[10px] text-white/60">AbracadaBâti</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Bouton artefacts */}
          <button
            onClick={() => setShowArtifacts(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
        </div>
      </div>
      
      {/* ═══ ZONE DE CHAT ═══ */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50">
        {messages.map((msg, idx) => (
          <div key={idx} className={`mb-4 ${msg.role === 'user' ? 'flex justify-end' : ''}`}>
            {msg.role === 'assistant' ? (
              <div className="flex gap-3 max-w-[90%]">
                {/* Avatar Assistant */}
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E8650A] to-[#F97316] flex items-center justify-center flex-shrink-0 shadow-md">
                  <Wand2 size={14} className="text-white" />
                </div>
                {/* Bulle message */}
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <p className="text-gray-800 text-[15px] leading-relaxed">{cleanMarkdown(msg.content)}</p>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%] shadow-md">
                <p className="text-[15px] leading-relaxed">{cleanMarkdown(msg.content)}</p>
              </div>
            )}
          </div>
        ))}
        
        {/* Suggestions si conversation vide */}
        {messages.length === 1 && (
          <div className="mt-4">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-3 px-1">Suggestions</p>
            <div className="space-y-2">
              {[
                "Crée-moi un devis pour une rénovation de salle de bain",
                "Trouve-moi un électricien à Paris",
                "Quelles sont mes tâches du jour ?",
                "Aide-moi à rédiger une annonce"
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setMessages([...messages, { role: 'user', content: suggestion }]);
                    setTimeout(() => {
                      setMessages(prev => [...prev, { 
                        role: 'assistant', 
                        content: 'Je travaille sur ta demande... (Ceci est un aperçu test)' 
                      }]);
                    }, 500);
                  }}
                  className="w-full text-left px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-[#E8650A] hover:shadow-md transition-all text-sm text-gray-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* ═══ BARRE DE SAISIE STYLE ABRACADAWORLD ═══ */}
      <div className="p-3 border-t border-gray-200 bg-white" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
        <div className="relative bg-gray-100 rounded-2xl border border-gray-200 focus-within:border-[#E8650A] focus-within:bg-white transition-all">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Message AbracadaBâti..."
            rows={1}
            className="w-full px-4 py-3 pr-24 bg-transparent resize-none focus:outline-none text-[15px] placeholder-gray-500"
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <Mic size={18} className="text-[#1E3A5F]" />
            </button>
            <button 
              className={`p-2 rounded-full transition-all ${inputValue.trim() ? 'bg-[#E8650A] hover:bg-[#d45a09]' : 'bg-gray-300'}`}
              onClick={() => {
                if (inputValue.trim()) {
                  setMessages([...messages, { role: 'user', content: inputValue }]);
                  setInputValue('');
                  setTimeout(() => {
                    setMessages(prev => [...prev, { 
                      role: 'assistant', 
                      content: 'Je comprends ta demande. Voici ce que je peux faire pour toi... (Aperçu test)' 
                    }]);
                  }, 500);
                }
              }}
            >
              <ChevronRight size={18} className={inputValue.trim() ? 'text-white' : 'text-gray-500'} />
            </button>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 text-center mt-2">AbracadaBâti peut faire des erreurs. Vérifie les informations importantes.</p>
      </div>
      
      {/* ═══ SIDEBAR ABRACADAWORLD (Projets en haut + Actions rapides) ═══ */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-10"
              onClick={() => setShowSidebar(false)}
            />
            {/* Sidebar */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-[300px] z-20 flex flex-col"
              style={{ background: 'linear-gradient(180deg, #1E3A5F 0%, #152a45 100%)' }}
            >
              {/* Header Sidebar */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E8650A] to-[#F97316] flex items-center justify-center">
                    <Wand2 size={16} className="text-white" />
                  </div>
                  <span className="text-white font-bold">AbracadaBâti</span>
                </div>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={18} className="text-white/70" />
                </button>
              </div>
              
              {/* ═══ BOUTONS ACTIONS RAPIDES ═══ */}
              <div className="p-3 border-b border-white/10">
                <p className="text-[10px] text-white/50 uppercase font-semibold mb-2 px-1">Actions rapides</p>
                <div className="grid grid-cols-4 gap-2">
                  <button className="flex flex-col items-center gap-1 p-2 hover:bg-white/10 rounded-xl transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Briefcase size={18} className="text-orange-400" />
                    </div>
                    <span className="text-[10px] text-white/80">Devis</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 hover:bg-white/10 rounded-xl transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Mic size={18} className="text-purple-400" />
                    </div>
                    <span className="text-[10px] text-white/80">Vocal</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 hover:bg-white/10 rounded-xl transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Calendar size={18} className="text-blue-400" />
                    </div>
                    <span className="text-[10px] text-white/80">Agenda</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 hover:bg-white/10 rounded-xl transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Image size={18} className="text-emerald-400" />
                    </div>
                    <span className="text-[10px] text-white/80">Publier</span>
                  </button>
                </div>
              </div>
              
              {/* ═══ PROJETS EN HAUT ═══ */}
              <div className="p-3 border-b border-white/10">
                <div className="flex items-center justify-between mb-2 px-1">
                  <p className="text-[10px] text-white/50 uppercase font-semibold">Mes Projets</p>
                  <button className="text-[10px] text-[#E8650A] font-medium">+ Nouveau</button>
                </div>
                <div className="space-y-1">
                  <button className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/10 rounded-xl text-white/90 text-sm transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <Building2 size={14} className="text-orange-400" />
                    </div>
                    <span className="truncate flex-1 text-left">Rénovation Villa Martin</span>
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/10 rounded-xl text-white/90 text-sm transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Building2 size={14} className="text-blue-400" />
                    </div>
                    <span className="truncate flex-1 text-left">Extension Garage Dubois</span>
                  </button>
                </div>
              </div>
              
              {/* Nouvelle conversation */}
              <div className="p-3">
                <button className="w-full flex items-center justify-center gap-2 px-3 py-3 bg-[#E8650A] hover:bg-[#d45a09] rounded-xl text-white text-sm font-semibold transition-colors shadow-lg">
                  <MessageCircle size={18} />
                  Nouvelle conversation
                </button>
              </div>
              
              {/* ═══ HISTORIQUE AVEC SUPPRESSION ═══ */}
              <div className="flex-1 overflow-y-auto px-3">
                {['Aujourd\'hui', 'Hier'].map(dateGroup => (
                  <div key={dateGroup}>
                    <p className="text-[10px] text-white/40 uppercase font-semibold mb-2 mt-3 px-2">{dateGroup}</p>
                    <div className="space-y-1">
                      {conversations.filter(c => c.date === dateGroup).map(conv => (
                        <div 
                          key={conv.id}
                          className="group flex items-center gap-2 px-3 py-2.5 hover:bg-white/10 rounded-xl transition-colors"
                        >
                          <MessageCircle size={14} className="text-white/40 flex-shrink-0" />
                          <span className="text-white/80 text-sm truncate flex-1">{conv.title}</span>
                          <button 
                            onClick={() => deleteConversation(conv.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                          >
                            <X size={14} className="text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Footer Sidebar */}
              <div className="p-3 border-t border-white/10">
                <button className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/10 rounded-xl text-white/60 text-sm transition-colors">
                  <User size={16} />
                  Mon compte
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* ═══ PANEL ARTEFACTS ABRACADAWORLD ═══ */}
      <AnimatePresence>
        {showArtifacts && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-10"
              onClick={() => setShowArtifacts(false)}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[300px] bg-white z-20 flex flex-col shadow-2xl"
            >
              {/* Header Panel */}
              <div 
                className="flex items-center justify-between p-4 border-b"
                style={{ 
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                  borderColor: '#E8650A'
                }}
              >
                <span className="font-bold text-white">Résultats</span>
                <button
                  onClick={() => setShowArtifacts(false)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={18} className="text-white/70" />
                </button>
              </div>
              
              {/* Contenu vide */}
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center bg-gray-50">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1E3A5F]/10 to-[#E8650A]/10 flex items-center justify-center mb-4">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <line x1="9" y1="9" x2="15" y2="9"/>
                    <line x1="9" y1="13" x2="15" y2="13"/>
                    <line x1="9" y1="17" x2="12" y2="17"/>
                  </svg>
                </div>
                <p className="text-[#1E3A5F] font-semibold mb-1">Tes résultats ici</p>
                <p className="text-gray-500 text-sm">Devis, tâches, documents générés apparaîtront ici</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
